import { useState, useMemo, useEffect } from "react";
import { Plus, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ViewToggle } from "@/components/transportation/ViewToggle";
import { TransportationFilters } from "@/components/transportation/TransportationFilters";
import { useCountries } from "@/hooks/useCountries";
import { useCities } from "@/hooks/useCities";
import { TourVehiclePricing, VehiclePricing } from "@/components/tourist-tours/TourVehiclePricing";
import { useTourVehiclePricing } from "@/hooks/useTourVehiclePricing";

interface TouristTour {
  id: string;
  sort_order: number;
  name_ar: string;
  name_en: string;
  description_ar: string;
  country_id: string | null;
  city_id: string | null;
  countries: {
    name_ar: string;
  } | null;
  cities: {
    name_ar: string;
  } | null;
}

export default function TouristTours() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPricingDialogOpen, setIsPricingDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<TouristTour | null>(null);
  const [selectedTourForPricing, setSelectedTourForPricing] = useState<TouristTour | null>(null);
  const [view, setView] = useState<'table' | 'cards'>('cards');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('sort_order');
  const [filterBy, setFilterBy] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedCity, setSelectedCity] = useState('all');
  const [vehiclePricing, setVehiclePricing] = useState<VehiclePricing[]>([]);
  const [formData, setFormData] = useState({
    sort_order: 1,
    name_ar: "",
    name_en: "",
    description_ar: "",
    country_id: "",
    city_id: ""
  });

  const queryClient = useQueryClient();
  
  const {
    data: tours = [],
    isLoading
  } = useQuery({
    queryKey: ['tourist-tours'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tourist_tours')
        .select(`
          *,
          countries(name_ar),
          cities(name_ar)
        `)
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data.map(item => ({
        ...item,
        countries: item.countries || null,
        cities: item.cities || null
      })) as TouristTour[];
    }
  });

  const { data: countries = [] } = useCountries();
  const { data: allCities = [] } = useCities();
  
  const { data: vehicleTypes = [] } = useQuery({
    queryKey: ['vehicle-types-for-tours'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vehicle_types')
        .select('id, name_ar')
        .order('sort_order');
      if (error) throw error;
      return data;
    }
  });

  // Hook للحصول على أسعار السيارات للجولة المحددة
  const {
    data: currentTourPricing = [],
    savePricing,
    isSaving
  } = useTourVehiclePricing(selectedTourForPricing?.id || null);

  const citiesForFilter = useMemo(() => {
    if (selectedCountry === 'all') return [];
    return allCities.filter(city => city.country_id === selectedCountry);
  }, [selectedCountry, allCities]);

  const citiesForDialog = useMemo(() => {
    if (!formData.country_id) return [];
    return allCities.filter(city => city.country_id === formData.country_id);
  }, [formData.country_id, allCities]);

  // فلترة وترتيب البيانات
  const filteredAndSortedTours = tours.filter(tour => {
    const matchesSearch = tour.name_ar.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         (tour.name_en && tour.name_en.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCountry = selectedCountry === 'all' || tour.country_id === selectedCountry;
    const matchesCity = selectedCity === 'all' || tour.city_id === selectedCity;
    return matchesSearch && matchesCountry && matchesCity;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'name_ar':
        return a.name_ar.localeCompare(b.name_ar, 'ar');
      case 'name_ar_desc':
        return b.name_ar.localeCompare(a.name_ar, 'ar');
      case 'sort_order':
      default:
        return a.sort_order - b.sort_order;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const { error } = await supabase.from('tourist_tours').insert([data]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tourist-tours'] });
      toast.success("تم إضافة الجولة السياحية بنجاح");
      resetForm();
    },
    onError: (error) => {
      toast.error("حدث خطأ أثناء إضافة الجولة");
      console.error(error);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      const { error } = await supabase.from('tourist_tours').update(data).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tourist-tours'] });
      toast.success("تم تحديث الجولة بنجاح");
      resetForm();
    },
    onError: (error) => {
      toast.error("حدث خطأ أثناء تحديث الجولة");
      console.error(error);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('tourist_tours').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tourist-tours'] });
      toast.success("تم حذف الجولة بنجاح");
    },
    onError: (error) => {
      toast.error("حدث خطأ أثناء حذف الجولة");
      console.error(error);
    }
  });

  const resetForm = () => {
    setFormData({
      sort_order: 1,
      name_ar: "",
      name_en: "",
      description_ar: "",
      country_id: "",
      city_id: ""
    });
    setEditingItem(null);
    setIsDialogOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (item: TouristTour) => {
    setEditingItem(item);
    setFormData({
      sort_order: item.sort_order,
      name_ar: item.name_ar,
      name_en: item.name_en || "",
      description_ar: item.description_ar || "",
      country_id: item.country_id || "",
      city_id: item.city_id || ""
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    if (confirm("هل أنت متأكد من حذف هذه الجولة؟")) {
      deleteMutation.mutate(id);
    }
  };

  const handleManagePricing = (tour: TouristTour) => {
    setSelectedTourForPricing(tour);
    setIsPricingDialogOpen(true);
  };

  const handleSavePricing = () => {
    if (selectedTourForPricing && vehiclePricing.length > 0) {
      savePricing({ tourId: selectedTourForPricing.id, pricing: vehiclePricing });
      toast.success("تم حفظ أسعار السيارات بنجاح");
      setIsPricingDialogOpen(false);
    }
  };

  const handleCountryChange = (value: string) => {
    setSelectedCountry(value);
    setSelectedCity('all');
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSortBy('sort_order');
    setFilterBy('all');
    setSelectedCountry('all');
    setSelectedCity('all');
  };

  // تحديث بيانات الأسعار عند تغيير الجولة المحددة
  useEffect(() => {
    if (currentTourPricing.length > 0) {
      const pricing: VehiclePricing[] = currentTourPricing.map(p => ({
        id: p.id,
        vehicle_type_id: p.vehicle_type_id,
        vehicle_type_name: p.vehicle_types?.name_ar,
        price: p.price,
        currency: p.currency
      }));
      setVehiclePricing(pricing);
    } else {
      setVehiclePricing([]);
    }
  }, [currentTourPricing]);

  if (isLoading) {
    return <div className="flex justify-center p-8">جاري التحميل...</div>;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">الجولات السياحية</h1>
        </div>
        <div className="flex gap-3">
          <ViewToggle view={view} onViewChange={setView} />
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => resetForm()}>
                <Plus className="w-4 h-4 mr-2" />
                إضافة جولة جديدة
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {editingItem ? "تعديل الجولة" : "إضافة جولة جديدة"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sort_order">الترتيب</Label>
                    <Input 
                      id="sort_order" 
                      type="number" 
                      min="1" 
                      value={formData.sort_order} 
                      onChange={e => setFormData({ ...formData, sort_order: parseInt(e.target.value) })} 
                      required 
                    />
                  </div>
                  <div>
                    <Label htmlFor="name_ar">الاسم بالعربية</Label>
                    <Input 
                      id="name_ar" 
                      value={formData.name_ar} 
                      onChange={e => setFormData({ ...formData, name_ar: e.target.value })} 
                      required 
                      placeholder="اسم الجولة" 
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="name_en">الاسم بالإنجليزية</Label>
                  <Input 
                    id="name_en" 
                    value={formData.name_en} 
                    onChange={e => setFormData({ ...formData, name_en: e.target.value })} 
                    placeholder="Tour name in English" 
                  />
                </div>
                <div>
                  <Label htmlFor="description_ar">الوصف بالعربية</Label>
                  <Textarea 
                    id="description_ar" 
                    value={formData.description_ar} 
                    onChange={e => setFormData({ ...formData, description_ar: e.target.value })} 
                    placeholder="وصف الجولة السياحية" 
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="country_id">الدولة</Label>
                    <Select 
                      value={formData.country_id} 
                      onValueChange={value => setFormData({ ...formData, country_id: value, city_id: "" })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="اختر الدولة" />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map(country => (
                          <SelectItem key={country.id} value={country.id}>
                            {country.name_ar}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="city_id">المدينة</Label>
                    <Select 
                      value={formData.city_id} 
                      onValueChange={value => setFormData({ ...formData, city_id: value })} 
                      disabled={!formData.country_id || citiesForDialog.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="اختر المدينة" />
                      </SelectTrigger>
                      <SelectContent>
                        {citiesForDialog.map(city => (
                          <SelectItem key={city.id} value={city.id}>
                            {city.name_ar}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    إلغاء
                  </Button>
                  <Button type="submit">
                    {editingItem ? "تحديث" : "إضافة"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <TransportationFilters 
        searchTerm={searchTerm} 
        onSearchChange={setSearchTerm} 
        sortBy={sortBy} 
        onSortChange={setSortBy} 
        filterBy={filterBy} 
        onFilterChange={setFilterBy} 
        onClearFilters={clearFilters} 
        countries={countries} 
        cities={citiesForFilter} 
        countryId={selectedCountry} 
        onCountryChange={handleCountryChange} 
        cityId={selectedCity} 
        onCityChange={setSelectedCity} 
      />

      {view === 'table' ? (
        <div className="border rounded-lg bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>الترتيب</TableHead>
                <TableHead>اسم الجولة</TableHead>
                <TableHead>الدولة</TableHead>
                <TableHead>المدينة</TableHead>
                <TableHead>العمليات</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedTours.map(tour => (
                <TableRow key={tour.id}>
                  <TableCell>
                    <Badge variant="outline">#{tour.sort_order}</Badge>
                  </TableCell>
                  <TableCell className="font-medium">{tour.name_ar}</TableCell>
                  <TableCell>{tour.countries?.name_ar || '-'}</TableCell>
                  <TableCell>{tour.cities?.name_ar || '-'}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleEdit(tour)}>
                        تعديل
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleManagePricing(tour)}
                      >
                        <Settings className="w-4 h-4 mr-1" />
                        إدارة الأسعار
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(tour.id)}>
                        حذف
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAndSortedTours.map(tour => (
            <Card key={tour.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="flex justify-between items-center">
                  <span>{tour.name_ar}</span>
                  <Badge variant="outline">#{tour.sort_order}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  {tour.countries?.name_ar && <p><strong>الدولة:</strong> {tour.countries.name_ar}</p>}
                  {tour.cities?.name_ar && <p><strong>المدينة:</strong> {tour.cities.name_ar}</p>}
                  {tour.description_ar && (
                    <p className="text-gray-500 line-clamp-2" title={tour.description_ar}>
                      {tour.description_ar}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" onClick={() => handleEdit(tour)} className="flex-1">
                    تعديل
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleManagePricing(tour)}
                    className="flex-1"
                  >
                    <Settings className="w-4 h-4 mr-1" />
                    الأسعار
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(tour.id)} className="flex-1">
                    حذف
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* حوار إدارة أسعار السيارات */}
      <Dialog open={isPricingDialogOpen} onOpenChange={setIsPricingDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              إدارة أسعار السيارات - {selectedTourForPricing?.name_ar}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <TourVehiclePricing
              vehiclePricing={vehiclePricing}
              onUpdate={setVehiclePricing}
              vehicleTypes={vehicleTypes}
            />
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => setIsPricingDialogOpen(false)}
              >
                إلغاء
              </Button>
              <Button 
                onClick={handleSavePricing}
                disabled={isSaving}
              >
                {isSaving ? "جاري الحفظ..." : "حفظ الأسعار"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {filteredAndSortedTours.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">لا توجد جولات متاحة حالياً</p>
        </div>
      )}
    </div>
  );
}